/**
 * Countries and currencies offered in the platform's own forms.
 *
 * Free-text country and currency codes are a reliable source of support
 * tickets: `UK` instead of `GB`, `EURO` instead of `eur`, a stray space. Stripe
 * rejects them at account creation, by which point the merchant record already
 * exists locally and someone has to clean it up.
 *
 * This list is the countries where Stripe Connect can create accounts. It is a
 * snapshot, not a live feed - Stripe remains the authority, and an account
 * creation can still fail if availability changes. That failure is surfaced
 * rather than swallowed.
 */
export const COUNTRIES = [
    { code: 'US', name: 'United States', currency: 'usd' },
    { code: 'GB', name: 'United Kingdom', currency: 'gbp' },
    { code: 'BG', name: 'Bulgaria', currency: 'eur' },
    { code: 'AT', name: 'Austria', currency: 'eur' },
    { code: 'AU', name: 'Australia', currency: 'aud' },
    { code: 'BE', name: 'Belgium', currency: 'eur' },
    { code: 'BR', name: 'Brazil', currency: 'brl' },
    { code: 'CA', name: 'Canada', currency: 'cad' },
    { code: 'HR', name: 'Croatia', currency: 'eur' },
    { code: 'CY', name: 'Cyprus', currency: 'eur' },
    { code: 'CZ', name: 'Czechia', currency: 'czk' },
    { code: 'DK', name: 'Denmark', currency: 'dkk' },
    { code: 'EE', name: 'Estonia', currency: 'eur' },
    { code: 'FI', name: 'Finland', currency: 'eur' },
    { code: 'FR', name: 'France', currency: 'eur' },
    { code: 'DE', name: 'Germany', currency: 'eur' },
    { code: 'GI', name: 'Gibraltar', currency: 'gbp' },
    { code: 'GR', name: 'Greece', currency: 'eur' },
    { code: 'HK', name: 'Hong Kong', currency: 'hkd' },
    { code: 'HU', name: 'Hungary', currency: 'huf' },
    { code: 'IE', name: 'Ireland', currency: 'eur' },
    { code: 'IT', name: 'Italy', currency: 'eur' },
    { code: 'JP', name: 'Japan', currency: 'jpy' },
    { code: 'LV', name: 'Latvia', currency: 'eur' },
    { code: 'LI', name: 'Liechtenstein', currency: 'chf' },
    { code: 'LT', name: 'Lithuania', currency: 'eur' },
    { code: 'LU', name: 'Luxembourg', currency: 'eur' },
    { code: 'MT', name: 'Malta', currency: 'eur' },
    { code: 'MX', name: 'Mexico', currency: 'mxn' },
    { code: 'NL', name: 'Netherlands', currency: 'eur' },
    { code: 'NZ', name: 'New Zealand', currency: 'nzd' },
    { code: 'NO', name: 'Norway', currency: 'nok' },
    { code: 'PL', name: 'Poland', currency: 'pln' },
    { code: 'PT', name: 'Portugal', currency: 'eur' },
    { code: 'RO', name: 'Romania', currency: 'ron' },
    { code: 'SG', name: 'Singapore', currency: 'sgd' },
    { code: 'SK', name: 'Slovakia', currency: 'eur' },
    { code: 'SI', name: 'Slovenia', currency: 'eur' },
    { code: 'ES', name: 'Spain', currency: 'eur' },
    { code: 'SE', name: 'Sweden', currency: 'sek' },
    { code: 'CH', name: 'Switzerland', currency: 'chf' },
    { code: 'AE', name: 'United Arab Emirates', currency: 'aed' },
];
export const CURRENCIES = [
    { code: 'usd', name: 'US Dollar', symbol: '$' },
    { code: 'eur', name: 'Euro', symbol: '€' },
    { code: 'gbp', name: 'British Pound', symbol: '£' },
    { code: 'aed', name: 'UAE Dirham', symbol: 'د.إ' },
    { code: 'aud', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'brl', name: 'Brazilian Real', symbol: 'R$' },
    { code: 'cad', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'chf', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'czk', name: 'Czech Koruna', symbol: 'Kč' },
    { code: 'dkk', name: 'Danish Krone', symbol: 'kr' },
    { code: 'hkd', name: 'Hong Kong Dollar', symbol: 'HK$' },
    { code: 'huf', name: 'Hungarian Forint', symbol: 'Ft' },
    { code: 'jpy', name: 'Japanese Yen', symbol: '¥' },
    { code: 'mxn', name: 'Mexican Peso', symbol: 'MX$' },
    { code: 'nok', name: 'Norwegian Krone', symbol: 'kr' },
    { code: 'nzd', name: 'New Zealand Dollar', symbol: 'NZ$' },
    { code: 'pln', name: 'Polish Zloty', symbol: 'zł' },
    { code: 'ron', name: 'Romanian Leu', symbol: 'lei' },
    { code: 'sek', name: 'Swedish Krona', symbol: 'kr' },
    { code: 'sgd', name: 'Singapore Dollar', symbol: 'S$' },
];
/**
 * Country and currency names in whatever language the panel is showing.
 *
 * The English names above stay as the fallback and as the canonical spelling in
 * logs, but a merchant in Sofia should read "България" and one in Berlin
 * "Deutschland". Intl.DisplayNames ships this data with the runtime, so there
 * is no translation table here to fall out of date.
 */
export function countryName(code, locale = 'en') {
    const upper = code.toUpperCase();
    try {
        const localised = new Intl.DisplayNames([locale], { type: 'region' }).of(upper);
        if (localised && localised !== upper)
            return localised;
    }
    catch {
        // Unknown locale or a runtime without the data: fall through to English.
    }
    return COUNTRIES.find((c) => c.code === upper)?.name ?? code;
}
export function currencyName(code, locale = 'en') {
    const upper = code.toUpperCase();
    try {
        const localised = new Intl.DisplayNames([locale], { type: 'currency' }).of(upper);
        if (localised && localised !== upper)
            return localised;
    }
    catch {
        // As above.
    }
    return CURRENCIES.find((c) => c.code === code.toLowerCase())?.name ?? code;
}
export function defaultCurrencyFor(countryCode) {
    return COUNTRIES.find((c) => c.code === countryCode.toUpperCase())?.currency ?? 'usd';
}
//# sourceMappingURL=regions.js.map